import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { map, timeout } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { getBrowser, getDeviceType, isDeviceId } from './device-info';
import { readDeviceIdCookie, writeDeviceIdCookie } from './device-id-cookie';

/** The id names the browser, not the admin session, so sign-out must not remove it. */
const DEVICE_ID_KEY = 'device_id';

/** The register call never blocks anything, so this only bounds how long it may stay pending. */
const REGISTER_TIMEOUT_MS = 8000;

interface DeviceRegisterResponse {
    data?: { device_id?: string };
}

/**
 * Identifies this browser to the backend's request log.
 *
 * At app start `start()` sends `POST device/register` (under `API_BASE_URL`, the admin router)
 * with the stored id or null; the backend returns the same id for a known device or issues a new
 * one. From then on `DeviceInfoInterceptor` sends `x-device-id`, `x-device-type` and `x-browser`
 * on every API request, which is also how the backend links the device to the admin at login.
 *
 * Monitoring only: nothing waits on this service, it shows nothing on screen, and every failure
 * leaves the app exactly as it would be without it. Off unless
 * `environment.DEVICE_TRACKING_ENABLED` is true.
 */
@Injectable({
    providedIn: 'root'
})
export class DeviceTrackingService {
    /**
     * Built on HttpBackend so the register call skips every interceptor (AccessTokenInterceptor
     * and the Fuse mock-API interceptor): it needs no Authorization header, and a failure on it
     * must stay invisible to the rest of the app.
     */
    private readonly http: HttpClient;
    private deviceId: string | null = null;
    private detailHeaders: Record<string, string> | null = null;
    private started = false;

    constructor(httpBackend: HttpBackend) {
        this.http = new HttpClient(httpBackend);
    }

    /**
     * Registers this browser once per app load and keeps the id the backend returns.
     * Fire-and-forget: until it answers, requests carry the stored id, or none on a first visit.
     */
    start(): void {
        if (!environment.DEVICE_TRACKING_ENABLED || this.started) return;
        this.started = true;

        this.http
            .post<DeviceRegisterResponse>(
                `${environment.API_BASE_URL}/device/register`,
                { device_id: this.getDeviceId() },
                { headers: this.getHeaders() }
            )
            .pipe(
                timeout(REGISTER_TIMEOUT_MS),
                map((response) => {
                    const deviceId = response?.data?.device_id;
                    if (!isDeviceId(deviceId)) {
                        throw new Error('Device register response carried no device_id');
                    }
                    return deviceId;
                })
            )
            .subscribe(
                (deviceId) => this.saveDeviceId(deviceId),
                (error) => console.error('Device register failed:', error)
            );
    }

    /**
     * The device headers for one API request. Synchronous, so the interceptor never waits.
     * @returns `x-device-type`, `x-browser`, and `x-device-id` once an id is known
     */
    getHeaders(): Record<string, string> {
        const headers = { ...this.getDetailHeaders() };
        const deviceId = this.getDeviceId();
        if (deviceId) headers['x-device-id'] = deviceId;
        return headers;
    }

    /** Device type and browser do not change within a page load, so they are read once. */
    private getDetailHeaders(): Record<string, string> {
        if (!this.detailHeaders) {
            const headers: Record<string, string> = { 'x-device-type': getDeviceType() };
            const browser = getBrowser();
            if (browser) headers['x-browser'] = browser;
            this.detailHeaders = headers;
        }
        return this.detailHeaders;
    }

    private getDeviceId(): string | null {
        if (!this.deviceId) this.deviceId = this.readStoredDeviceId();
        return this.deviceId;
    }

    /**
     * localStorage first; the cookie backup covers every `localStorage.clear()` in this app, and
     * puts the id back into localStorage when it finds it there.
     */
    private readStoredDeviceId(): string | null {
        try {
            const stored = localStorage.getItem(DEVICE_ID_KEY);
            if (isDeviceId(stored)) return stored;
        } catch {
            // localStorage unavailable (blocked by the browser): fall back to the cookie.
        }
        const backup = readDeviceIdCookie();
        if (backup) this.storeLocally(backup);
        return backup;
    }

    private saveDeviceId(deviceId: string): void {
        this.deviceId = deviceId;
        this.storeLocally(deviceId);
        writeDeviceIdCookie(deviceId);
    }

    private storeLocally(deviceId: string): void {
        try {
            localStorage.setItem(DEVICE_ID_KEY, deviceId);
        } catch {
            // localStorage unavailable: the in-memory id and the cookie still hold it.
        }
    }
}
